import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  Form,
  Button,
  Alert,
  Container,
  Spinner,
  Row,
  Col
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import SettingsPageHeader from '../components/SettingsPageHeader';
import { AssignmentIcon } from '../components/icons/SettingsIcons';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { authAPI, nurseryAPI } from '../utils/api';
import type {
  CreateUserNurseryAssignmentRequest,
  User,
  Nursery,
  Role
} from '../types/auth';

const UserNurseryAssignments: React.FC = () => {
  const { currentNursery, userRolePermissionsLoading } = useAuth();
  const {
    isSuperAdmin,
    canViewUsers,
    canViewRoles,
    canAssignUsers,
    canViewNurseries,
    canAccessUserManagement
  } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'userNurseryAssignments' });
  const { t: tc } = useTranslation('common');

  const isCurrentUserSuperAdmin = isSuperAdmin;

  const [assignmentFormData, setAssignmentFormData] = useState<CreateUserNurseryAssignmentRequest>({
    user_id: 0,
    nursery_id: '',
    role_id: 0
  });

  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentSuccess, setAssignmentSuccess] = useState<string | null>(null);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);

  const [nurseryUsers, setNurseryUsers] = useState<User[]>([]);
  const [allNurseries, setAllNurseries] = useState<Nursery[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [allNurseriesLoading, setAllNurseriesLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);

  const loadUsersForNursery = useCallback(async (nurseryId: string) => {
    if (!nurseryId) {
      setNurseryUsers([]);
      return;
    }

    try {
      setUsersLoading(true);
      const response = isCurrentUserSuperAdmin
        ? await authAPI.getUsersGlobalList(nurseryId)
        : await authAPI.getUsers(nurseryId);

      if (response.success) {
        let filteredUsers = response.data;
        if (!isCurrentUserSuperAdmin) {
          filteredUsers = response.data.filter(user => user.superAdminFlag !== 1);
        }
        setNurseryUsers(filteredUsers);
      }
    } catch (error: unknown) {
      console.error('Error loading users for nursery:', error);
      setAssignmentError(t('loadUsersFailed'));
    } finally {
      setUsersLoading(false);
    }
  }, [isCurrentUserSuperAdmin, t]);

  const loadAllNurseries = useCallback(async () => {
    try {
      setAllNurseriesLoading(true);
      const response = await nurseryAPI.getNurseries();

      if (response.success) {
        setAllNurseries(response.data);
      }
    } catch (error: unknown) {
      console.error('Error loading nurseries:', error);
      setAssignmentError(t('loadNurseriesFailed'));
    } finally {
      setAllNurseriesLoading(false);
    }
  }, [t]);

  const loadRoles = useCallback(async (nurseryId: string) => {
    if (!nurseryId) {
      setAvailableRoles([]);
      return;
    }

    try {
      setRolesLoading(true);
      const response = await authAPI.getRoles(nurseryId);

      if (response.success) {
        setAvailableRoles(response.data);
      }
    } catch (error: unknown) {
      console.error('Error loading roles:', error);
      setAssignmentError(t('loadRolesFailed'));
    } finally {
      setRolesLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (canViewNurseries) {
      loadAllNurseries();
    }
  }, [loadAllNurseries, canViewNurseries]);

  useEffect(() => {
    if (assignmentFormData.nursery_id) {
      if (canViewUsers) {
        loadUsersForNursery(assignmentFormData.nursery_id);
      }
      if (canViewRoles) {
        loadRoles(assignmentFormData.nursery_id);
      }
    } else {
      setNurseryUsers([]);
      setAvailableRoles([]);
    }
  }, [assignmentFormData.nursery_id, loadUsersForNursery, loadRoles, canViewUsers, canViewRoles]);

  const handleAssignmentFormChange = useCallback((field: keyof CreateUserNurseryAssignmentRequest, value: string | number) => {
    setAssignmentFormData(prev => {
      const newData = { ...prev, [field]: value };

      if (field === 'nursery_id') {
        newData.user_id = 0;
        newData.role_id = 0;
      }
      else if (field === 'user_id') {
        newData.role_id = 0;
      }

      return newData;
    });

    if (assignmentSuccess) setAssignmentSuccess(null);
    if (assignmentError) setAssignmentError(null);
  }, [assignmentSuccess, assignmentError]);

  const resetAssignmentForm = useCallback(() => {
    setAssignmentFormData({
      user_id: 0,
      nursery_id: '',
      role_id: 0
    });
    setNurseryUsers([]);
    setAvailableRoles([]);
  }, []);

  const handleAssignmentSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!assignmentFormData.nursery_id) {
      setAssignmentError(t('selectNurseryFirst'));
      return;
    }

    if (!assignmentFormData.user_id) {
      setAssignmentError(t('selectUser'));
      return;
    }

    if (!assignmentFormData.role_id) {
      setAssignmentError(t('selectRole'));
      return;
    }

    try {
      setAssignmentLoading(true);
      setAssignmentError(null);
      setAssignmentSuccess(null);

      const response = await authAPI.createUserNurseryAssignment(assignmentFormData);

      setAssignmentSuccess(response.message || t('createSuccess'));
      resetAssignmentForm();

    } catch (error: unknown) {
      console.error('Error creating assignment:', error);
      const err = error as { response?: { data?: { message?: string }; status?: number }; message?: string };
      setAssignmentError(err.response?.data?.message || err.message || t('saveFailed'));
    } finally {
      setAssignmentLoading(false);
    }
  }, [assignmentFormData, resetAssignmentForm, t]);

  const settingsPath = currentNursery ? `/nursery/${currentNursery.nursery_id}/settings` : '/settings';

  if (userRolePermissionsLoading) {
    return (
      <Layout title={t('title')}>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
        </Container>
      </Layout>
    );
  }

  if (!canAccessUserManagement || !canAssignUsers) {
    return (
      <Layout title={t('title')}>
        <Container className="py-5">
          <Alert variant="danger" role="alert">
            {t('accessDenied')}
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="px-3 py-4">
        <SettingsPageHeader
          title={t('title')}
          settingsPath={settingsPath}
        />

        <Card className="shadow-sm">
          <Card.Body className="p-4">
            <div className="d-flex align-items-center mb-4">
              <AssignmentIcon />
              <h5 className="fw-medium text-secondary mb-0">{t('createAssignmentHeading')}</h5>
            </div>

            {assignmentSuccess && (
              <Alert variant="success" role="status" onClose={() => setAssignmentSuccess(null)} dismissible>
                {assignmentSuccess}
              </Alert>
            )}

            {assignmentError && (
              <Alert variant="danger" role="alert" onClose={() => setAssignmentError(null)} dismissible>
                {assignmentError}
              </Alert>
            )}

            <Form onSubmit={handleAssignmentSubmit} autoComplete="off">
              <Row className="g-3">
                <Col xs={12} md={6} lg={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark">{t('selectNurseryLabel')}</Form.Label>
                    <Form.Select
                      value={assignmentFormData.nursery_id}
                      onChange={(e) => handleAssignmentFormChange('nursery_id', e.target.value)}
                      disabled={assignmentLoading || allNurseriesLoading}
                      aria-label={t('selectNurseryLabel')}
                    >
                      <option value="">{t('chooseNursery')}</option>
                      {allNurseries.map((nursery) => (
                        <option key={nursery.nursery_id} value={nursery.nursery_id}>
                          {nursery.name || nursery.nursery_id} (ID: {nursery.nursery_id})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark">{t('selectUserLabel')}</Form.Label>
                    <Form.Select
                      value={assignmentFormData.user_id}
                      onChange={(e) => handleAssignmentFormChange('user_id', Number(e.target.value))}
                      disabled={assignmentLoading || usersLoading || !assignmentFormData.nursery_id}
                      aria-label={t('selectUserLabel')}
                    >
                      <option value={0}>
                        {assignmentFormData.nursery_id ? t('chooseUser') : t('selectNurseryFirst')}
                      </option>
                      {nurseryUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.email}) {user.role_name ? `- ${user.role_name}` : ''} {user.superAdminFlag === 1 ? `(${t('superAdmin')})` : ''}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark">{t('selectRoleLabel')}</Form.Label>
                    <Form.Select
                      value={assignmentFormData.role_id}
                      onChange={(e) => handleAssignmentFormChange('role_id', Number(e.target.value))}
                      disabled={assignmentLoading || rolesLoading || !assignmentFormData.nursery_id}
                      aria-label={t('selectRoleLabel')}
                    >
                      <option value={0}>
                        {assignmentFormData.nursery_id ? t('chooseRole') : t('selectNurseryFirst')}
                      </option>
                      {availableRoles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.role_name} ({tc('labels.nursery')}: {role.nursery_id})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex flex-column flex-sm-row gap-2 justify-content-end mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={resetAssignmentForm}
                  disabled={assignmentLoading}
                >
                  {t('reset')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={assignmentLoading}
                >
                  {assignmentLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('assigning')}
                    </>
                  ) : (
                    <>
                      <AssignmentIcon />
                      {t('createAssignmentBtn')}
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </Layout>
  );
};

export default UserNurseryAssignments;
